import { useEffect, useState } from 'react';

import {
  FireflyTertiaryButtonMenuItem,
  coalesceNoteContentToHtml,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';

import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { capitalizeFirstLetter } from '~/lib/common';
import { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { getParsedNoteActivityDate } from '~/lib/util/patient.util';
import { useGetNoteQuery } from '~/store/services/notes/notes';

type Props = {
  appointmentDetails: AppointmentDetailResponse | null;
  confirmedAttendees: GroupClassEventAttendee[];
  noteId: string;
  declinedUsers?: GroupClassEventAttendee[];
  groupClassEventDetails?: GroupClassEventDetails;
  setLastSavedTime: (lastSavedTime: DateTime) => void;
  setNoteId: (noteId: string) => void;
  setUploadedFiles: (uploadedFiles: AttachmentType[]) => void;
};

export const useGetNoteInitializer = ({
  appointmentDetails,
  confirmedAttendees,
  noteId,
  declinedUsers,
  groupClassEventDetails,
  setLastSavedTime,
  setNoteId,
  setUploadedFiles,
}: Props) => {
  const { data: getNoteData, isLoading: isGetNoteDataLoading } =
    useGetNoteQuery(noteId, { skip: !noteId });

  const { noteTemplateRevisions } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      getNoteData?.noteTemplateRevisionId,
    );
  const [initialValues, setInitialValues] = useState<EditNoteFormData>({
    aiSummaryId: null,
    activity: [],
    activityDate: DateTime.now().toJSDate(),
    addendumContent: '',
    appointment: null,
    attendees: [],
    declinedUsers: [],
    isConfidential: false,
    medium: null,
    noteTemplateRevision: null,
    noteTemplateRevisionData: null,
    sessionSummary: null,
    title: '',
    type: null,
  });

  useEffect(() => {
    if (getNoteData) {
      const {
        aiSummaryId,
        appointmentExternalId,
        addendumContent,
        attachments,
        isConfidential,
        noteCommunicationMethod,
        noteContent,
        noteExternalId,
        noteTemplateRevisionId,
        noteTemplateRevisionData,
        noteType,
        sessionSummary,
        title,
      } = getNoteData;

      setNoteId(noteExternalId);
      setLastSavedTime(DateTime.now());
      let revisionOption: FireflyTertiaryButtonMenuItem =
        NO_NOTE_TEMPLATE_OPTION;
      if (noteTemplateRevisions && noteTemplateRevisionId !== null) {
        const selectedNoteTemplateLabel = noteTemplateRevisions.find(
          (rev) => rev.templateRevisionId === noteTemplateRevisionId,
        )?.template.templateTitle;

        revisionOption = {
          node: noteTemplateRevisionId,
          label: selectedNoteTemplateLabel,
        };
      }

      const titleOrApptLabelByDefault = isEmpty(title)
        ? appointmentDetails?.appointmentTypeLabel
        : title || '';

      setInitialValues({
        aiSummaryId,
        activity: (getNoteData.activityWith ?? []).map((activityWithItem) => ({
          code: activityWithItem?.code,
          label: capitalizeFirstLetter(activityWithItem.activityWithName),
          value: capitalizeFirstLetter(activityWithItem.activityWithName),
        })),
        activityDate: getParsedNoteActivityDate(getNoteData.activityDate),
        addendumContent: coalesceNoteContentToHtml(addendumContent ?? ''),
        appointment: appointmentExternalId
          ? { label: '', node: appointmentExternalId }
          : null,
        attendees: confirmedAttendees,
        declinedUsers: declinedUsers ?? groupClassEventDetails?.declinedUsers,
        isConfidential,
        medium: noteCommunicationMethod
          ? {
              label: capitalizeFirstLetter(noteCommunicationMethod),
              value: capitalizeFirstLetter(noteCommunicationMethod),
            }
          : null,
        noteTemplateRevision: revisionOption,
        noteTemplateRevisionData,
        noteContentCache: noteContent,
        sessionSummary: sessionSummary || null,
        title: titleOrApptLabelByDefault,
        type: noteType
          ? {
              label: capitalizeFirstLetter(noteType),
              value: capitalizeFirstLetter(noteType),
            }
          : null,
      });
      setUploadedFiles(attachments);
    }
  }, [getNoteData, confirmedAttendees, noteTemplateRevisions]);

  return {
    initialValues,
    isGetNoteDataLoading,
    authoredByProviderExternalId: getNoteData?.authoredByProviderExternalId,
  };
};
